import React, { createContext, useContext, useState, useMemo, type ReactNode } from 'react';
import type { ActionPlan, Action, Policy, PolicyArea } from '../models/actionPlan';
import { useRouter } from 'next/router';
import { useActionDataAPICall } from '@/lib/hooks';

// Context properties interface
interface ActionPlanContextProps {
    data: ActionPlan | null;
    loading: boolean;
    error: string | null;
    getActionsByPolicy: (policyNo: string) => Action[];
    getPoliciesByAction: (actionNo: string) => Policy[];
    filterActionsByInstitutions: (institutions: string[]) => Action[];
    getUniqueResponsibleInstitutions: () => string[];
    getUniqueRelatedInstitutions: () => string[];
    searchActions: (searchTerm: string) => Action[];
    searchPolicies: (searchTerm: string) => Policy[];
    searchAndFilter: (searchTerm: string, institutions: string[]) => FilteredResult[];
}

// Initialize context with undefined as default value
const ActionPlanContext = createContext<ActionPlanContextProps | undefined>(undefined);

// Custom hook to use the context
export const useActionPlanContext = (): ActionPlanContextProps => {
    const context = useContext(ActionPlanContext);
    if (!context) {
        throw new Error('useActionPlanContext must be used within an ActionPlanProvider');
    }
    return context;
};

// Provider component definition
interface ActionPlanProviderProps {
    children: ReactNode;
}

interface FilteredResult {
    policyArea: PolicyArea;
    policies: {
        policy: Policy;
        actions: Action[];
    }[];
}

export const ActionPlanProvider: React.FC<ActionPlanProviderProps> = ({ children }) => {
    const { locale } = useRouter();

    // const [data, setData] = useState<ActionPlan | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);

    const getActionData = useActionDataAPICall('term')
    const data = useMemo(() => {
        if (!getActionData.data?.data) {
            return null
        }

        const actionPlanData: ActionPlan = {
            actionPlanData: locale === "tr" ? getActionData.data?.data.tr : getActionData.data?.data.en
        }
        setLoading(false);
        setError(null);
        return actionPlanData

    }, [getActionData.data])

    // useEffect(() => {
    //     const fetchData = async () => {
    //         setLoading(true);
    //         try {
    //             const result: ActionPlan = {
    //                 actionPlanData: content.actionPlanData
    //             };
    //             // setData(result);
    //         } catch (err) {
    //             setError((err as Error).message);
    //         } finally {
    //             setLoading(false);
    //         }
    //     };

    //     fetchData();
    // }, [locale]);

    const actions = useMemo(() => data?.actionPlanData.actions || [], [data]);
    const policies = useMemo(() => data?.actionPlanData.policies || [], [data]);
    const policyAreas = useMemo(() => data?.actionPlanData.policy_areas || [], [data]);

    // Nested search and filter function
    const searchAndFilter = (searchTerm: string, institutions: string[]): FilteredResult[] => {
        // Eğer hem searchTerm hem de institutions boşsa, tüm verileri döndür.
        if (searchTerm === '' && institutions.length === 0) {
            return policyAreas.map(policyArea => {
                const relatedPolicies = getPoliciesByAction(policyArea.area_no);
                const policiesWithActions = relatedPolicies.map(policy => ({
                    policy,
                    actions: getActionsByPolicy(policy.policy_no)
                }));
                return {
                    policyArea,
                    policies: policiesWithActions
                };
            });
        }

        // Step 1: Filter actions based on search term and institutions
        const filteredActions = actions.filter(action => {
            const matchesInstitution = institutions.length === 0 ||
                institutions.includes(action.responsible_institution || "") ||
                (action.related_institutions && action.related_institutions.split(',').some(inst => institutions.includes(inst.trim())));

            const matchesSearchTerm = searchTerm === '' ||
                action.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
                action.description?.toLowerCase().includes(searchTerm.toLowerCase());

            return matchesInstitution && matchesSearchTerm;
        });

        // Step 2: Create a map of action numbers to actions for faster lookup
        const actionMap = filteredActions.reduce((map, action) => {
            map[action.action_no] = action;
            return map;
        }, {} as { [key: string]: Action });

        // Step 3: Find policies that are linked to the filtered actions
        const filteredPolicies = policies.filter(policy => {
            const relatedActions = getActionsByPolicy(policy.policy_no); // Policy'ye ait Action'ları bul
            return relatedActions.some(action => actionMap[action.action_no]); // En az bir eşleşen Action varsa, bu Policy dahil edilir
        });

        // Step 4: Create a map of policy numbers to policies for faster lookup
        const policyMap = filteredPolicies.reduce((map, policy) => {
            map[policy.policy_no] = policy;
            return map;
        }, {} as { [key: string]: Policy });

        // Step 5: Filter policy areas that contain the filtered policies
        return policyAreas.map(policyArea => {
            const relatedPolicies = getPoliciesByAction(policyArea.area_no);
            const matchedPolicies = relatedPolicies.filter(policy => policyMap[policy.policy_no]);

            // Step 6: For each matched policy, find its corresponding actions
            const policiesWithActions = matchedPolicies.map(policy => ({
                policy,
                actions: getActionsByPolicy(policy.policy_no).filter(action => actionMap[action.action_no])
            }));

            return {
                policyArea,
                policies: policiesWithActions
            };
        }).filter(result => result.policies.length > 0); // Only return areas that have matched policies
    };

    // Get unique responsible institutions
    const uniqueResponsibleInstitutions = useMemo(() => {
        const responsibleInstitutions = actions
            .map((action) => action.responsible_institution)
            .filter((institution): institution is string => institution !== null); // Filter out null values
        return Array.from(new Set(responsibleInstitutions)).sort(); // Remove duplicates and return unique values
    }, [actions]);

    // Get unique related institutions
    const uniqueRelatedInstitutions = useMemo(() => {
        const relatedInstitutions = actions.flatMap((action) => {
            return action.related_institutions
                ? action.related_institutions.split(',').map(i => i.trim()).filter((i): i is string => i !== null)
                : [];
        });
        return Array.from(new Set(relatedInstitutions)).sort(); // Remove duplicates and return unique values
    }, [actions]);

    // Methods for unique institution retrieval
    const getUniqueResponsibleInstitutions = (): string[] => {
        return uniqueResponsibleInstitutions;
    };

    const getUniqueRelatedInstitutions = (): string[] => {
        return uniqueRelatedInstitutions;
    };

    const getActionsByPolicy = (policyNo: string): Action[] => {
        return actions.filter((action) => action.action_no.indexOf(`${policyNo}.`) > -1);
    };

    const getPoliciesByAction = (actionNo: string): Policy[] => {
        return policies.filter((policy) => policy.policy_no.toString().indexOf(`${actionNo}.`) > -1);
    };

    const filterActionsByInstitutions = (institutions: string[]): Action[] => {
        return actions.filter((action) => institutions.includes(action.responsible_institution || ""));
    };

    const searchActions = (searchTerm: string): Action[] => {
        return actions.filter(
            (action) =>
                action.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
                action.description?.toLowerCase().includes(searchTerm.toLowerCase())
        );
    };

    const searchPolicies = (searchTerm: string): Policy[] => {
        return policies.filter((policy) => policy.policy.toLowerCase().includes(searchTerm.toLowerCase()));
    };

    return (
        <ActionPlanContext.Provider
            value={{
                data,
                loading,
                error,
                getActionsByPolicy,
                getPoliciesByAction,
                filterActionsByInstitutions,
                getUniqueResponsibleInstitutions,
                getUniqueRelatedInstitutions,
                searchActions,
                searchPolicies,
                searchAndFilter
            }}
        >
            {children}
        </ActionPlanContext.Provider>
    );
};
